/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { FormPropTypes } from '@instructure/ui-form-field';
import { I18nPropTypes } from '@instructure/ui-i18n';
import PropTypes from 'prop-types';
import { controllable } from '@instructure/ui-prop-types';
const hourMinuteValidator = function (props, propName, componentName, location) {
  const propValue = props[propName];
  if (typeof propValue === 'undefined' || propValue === '') return null;
  const hourMinuteRegex = /^\d{2}:\d{2}$/;
  if (typeof propValue === 'string' && !propValue.match(hourMinuteRegex)) {
    return new Error(`Invalid ${location} \`${propName}\` \`${propValue}\` supplied to \`${componentName}\`, expected ` + `a HH:MM formatted string.`);
  }
  return null;
};
const propTypes = {
  description: PropTypes.node.isRequired,
  dateRenderLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  prevMonthLabel: PropTypes.string.isRequired,
  nextMonthLabel: PropTypes.string.isRequired,
  datePlaceholder: PropTypes.string,
  timePlaceholder: PropTypes.string,
  dateFormat: PropTypes.string,
  interaction: PropTypes.oneOf(['enabled', 'disabled', 'readonly']),
  timeRenderLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  timeStep: PropTypes.oneOf([5, 10, 15, 20, 30, 60]),
  timeFormat: PropTypes.string,
  locale: PropTypes.string,
  timezone: PropTypes.string,
  invalidDateTimeMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  showMessages: PropTypes.bool,
  messages: PropTypes.arrayOf(FormPropTypes.message),
  messageFormat: PropTypes.string,
  layout: PropTypes.oneOf(['stacked', 'columns', 'inline']),
  rowSpacing: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  colSpacing: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  value: controllable(I18nPropTypes.iso8601, 'onChange'),
  defaultValue: I18nPropTypes.iso8601,
  renderWeekdayLabels: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.node])),
  isRequired: PropTypes.bool,
  onChange: PropTypes.func,
  dateInputRef: PropTypes.func,
  timeInputRef: PropTypes.func,
  onBlur: PropTypes.func,
  disabledDates: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.string)]),
  disabledDateTimeMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  allowNonStepInput: PropTypes.bool,
  reset: PropTypes.func,
  initialTimeForNewDate: hourMinuteValidator
};
const allowedProps = ['description', 'dateRenderLabel', 'prevMonthLabel', 'nextMonthLabel', 'datePlaceholder', 'timePlaceholder', 'dateFormat', 'interaction', 'timeRenderLabel', 'timeStep', 'timeFormat', 'locale', 'timezone', 'invalidDateTimeMessage', 'showMessages', 'messages', 'messageFormat', 'layout', 'rowSpacing', 'colSpacing', 'value', 'defaultValue', 'renderWeekdayLabels', 'isRequired', 'onChange', 'dateInputRef', 'timeInputRef', 'onBlur', 'disabledDates', 'disabledDateTimeMessage', 'allowNonStepInput', 'reset'];
export { propTypes, allowedProps };